import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import FoundingMemberCounter from './FoundingMemberCounter';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'homeowner' | 'business'>('homeowner');
  const [counters, setCounters] = useState({ spots: 0, cities: 0 });
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!statsVisible) return;
    const targets = { spots: 24, cities: 15 };
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const ease = 1 - Math.pow(1 - progress, 3);
      setCounters({
        spots: Math.round(targets.spots * ease),
        cities: Math.round(targets.cities * ease),
      });
      if (step >= steps) clearInterval(timer);
    }, interval);
    return () => clearInterval(timer);
  }, [statsVisible]);

  const homeownerSteps = [
    { icon: '📋', title: 'Post Your Project', desc: 'Describe what you need and your budget in under 60 seconds. No phone calls required.' },
    { icon: '🤖', title: 'AI Matches You', desc: 'Our AI reads your request and routes it only to businesses with the right skills in your area.' },
    { icon: '💰', title: 'Pick the Best Deal', desc: 'Compare real quotes from vetted local pros. You choose. No pressure, no hidden fees.' },
  ];

  const businessSteps = [
    { icon: '⚡', title: 'Get Early Access', desc: 'Premium members see new jobs 30 minutes before Pro and 2 hours before Starter. Be first — win more.' },
    { icon: '🎯', title: 'Only Relevant Jobs', desc: "AI matches you to customers who need exactly what you do. No sifting through junk." },
    { icon: '📈', title: 'Grow Your Business', desc: 'Businesses pay a flat monthly plan, or nothing on Starter. There are no per-lead fees.' },
  ];

  return (
    <div style={styles.root}>
      <style>{`
        @media (max-width: 768px) {
          .steps-grid { grid-template-columns: 1fr !important; }
          .step-connector { display: none !important; }
          .why-grid { grid-template-columns: 1fr !important; }
          .pricing-grid { grid-template-columns: 1fr !important; }
          .pricing-card-featured { transform: scale(1) !important; }
          .final-cta-btns { flex-direction: column !important; align-items: center !important; }
          .section-container { padding: 0 16px !important; }
        }
      `}</style>
      {/* HERO */}
      <section style={styles.hero}>
        <div style={{ ...styles.heroBg, transform: `translateY(${scrollY * 0.4}px)` }} />
        <div style={styles.heroGradient} />
        <div style={styles.heroContent}>
          <div style={styles.heroBadge}>Founding Member pricing — first 24 paid Founding Members only</div>
          <FoundingMemberCounter tone="onDark" />
          <h1 style={styles.heroTitle}>
            The Smarter Way to<br />
            <span style={styles.heroAccent}>Connect & Get Hired</span>
          </h1>
          <p style={styles.heroSubtitle}>
            Homeowners post. AI matches. Local businesses compete.<br />
            No lead fees. No wasted time. Just results.
          </p>
          <div style={styles.heroTabs}>
            <button style={{ ...styles.heroTab, ...(activeTab === 'homeowner' ? styles.heroTabActive : {}) }} onClick={() => setActiveTab('homeowner')}>
              👤 I Need a Service
            </button>
            <button style={{ ...styles.heroTab, ...(activeTab === 'business' ? styles.heroTabActive : {}) }} onClick={() => setActiveTab('business')}>
              🏢 I'm a Business
            </button>
          </div>
          {activeTab === 'homeowner' ? (
            <div style={styles.heroCtaGroup}>
              <Link to="/new-request" style={styles.heroPrimaryBtn}>Post a Request — It's Free →</Link>
              <p style={styles.heroCtaNote}>No account needed to browse. Customers always free.</p>
            </div>
          ) : (
            <div style={styles.heroCtaGroup}>
              <Link to="/register?type=business" style={styles.heroPrimaryBtn}>Claim Founding Member Offer →</Link>
              <p style={styles.heroCtaNote}>50% off Pro or Premium for the life of your active subscription. Starter plan always free.</p>
            </div>
          )}
        </div>
        <div style={styles.heroScroll}>
          <div style={styles.heroScrollDot} />
          <span style={styles.heroScrollText}>Scroll to explore</span>
        </div>
      </section>

      {/* STATS */}
      <section ref={statsRef} style={styles.statsSection}>
        <div style={styles.statsBanner}>Looking for new businesses daily</div>
        <div style={styles.statsSubheader}>QuoteBid at a glance</div>
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statNumber}>$0</div>
            <div style={styles.statLabel}>Per-lead fees</div>
          </div>
          <div style={styles.statDivider} />
          <div style={styles.statCard}>
            <div style={styles.statNumber}>{counters.spots}</div>
            <div style={styles.statLabel}>Founding Member spots</div>
          </div>
          <div style={styles.statDivider} />
          <div style={styles.statCard}>
            <div style={styles.statNumber}>{counters.cities}</div>
            <div style={styles.statLabel}>Baton Rouge cities</div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section style={styles.howSection}>
        <div className="section-container" style={styles.sectionContainer}>
          <div style={styles.sectionLabel}>HOW IT WORKS</div>
          <h2 style={styles.sectionTitle}>{activeTab === 'homeowner' ? 'Get Quotes in Minutes' : 'Win More Jobs Every Week'}</h2>
          <div style={styles.howTabs}>
            <button style={{ ...styles.howTab, ...(activeTab === 'homeowner' ? styles.howTabActive : {}) }} onClick={() => setActiveTab('homeowner')}>For Homeowners</button>
            <button style={{ ...styles.howTab, ...(activeTab === 'business' ? styles.howTabActive : {}) }} onClick={() => setActiveTab('business')}>For Businesses</button>
          </div>
          <div className="steps-grid" style={styles.stepsGrid}>
            {(activeTab === 'homeowner' ? homeownerSteps : businessSteps).map((step, i) => (
              <div key={i} style={styles.stepCard}>
                <div style={styles.stepNumber}>{String(i + 1).padStart(2, '0')}</div>
                <div style={styles.stepIcon}>{step.icon}</div>
                <h3 style={styles.stepTitle}>{step.title}</h3>
                <p style={styles.stepDesc}>{step.desc}</p>
                {i < 2 && <div style={styles.stepConnector} />}
              </div>
            ))}
          </div>
          <div style={styles.howCta}>
            {activeTab === 'homeowner' ? (
              <Link to="/new-request" style={styles.primaryBtn}>Post Your Project Free →</Link>
            ) : (
              <Link to="/register?type=business" style={styles.primaryBtn}>Start Getting Leads →</Link>
            )}
          </div>
        </div>
      </section>

      {/* WHY QUOTEBID */}
      <section style={styles.whySection}>
        <div className="section-container" style={styles.sectionContainer}>
          <div style={styles.sectionLabel}>WHY QUOTEBID</div>
          <h2 style={styles.sectionTitle}>The Platform Built Different</h2>
          <div className="why-grid" style={styles.whyGrid}>
            <div style={styles.whyCard}>
              <div style={styles.whyIcon}>🎯</div>
              <h3 style={styles.whyTitle}>AI-Powered Matching</h3>
              <p style={styles.whyDesc}>Our AI reads every request and matches it to businesses with the exact right skills. No more irrelevant leads. No more wasted time.</p>
            </div>
            <div style={styles.whyCard}>
              <div style={styles.whyIcon}>⏱️</div>
              <h3 style={styles.whyTitle}>Tiered Notification System</h3>
              <p style={styles.whyDesc}>Premium members get a 30-minute head start on new jobs over Pro members, who get notified well ahead of free members. First in wins.</p>
            </div>
            <div style={styles.whyCard}>
              <div style={styles.whyIcon}>💵</div>
              <h3 style={styles.whyTitle}>No Pay-Per-Lead Trap</h3>
              <p style={styles.whyDesc}>Unlike Angi and HomeAdvisor, we don't charge you $50-100 per lead whether you win or not. Flat monthly plans. Predictable costs.</p>
            </div>
            <div style={styles.whyCard}>
              <div style={styles.whyIcon}>🏆</div>
              <h3 style={styles.whyTitle}>Real Competition Wins</h3>
              <p style={styles.whyDesc}>Customers pick the best quote. Businesses that deliver value win. Seat limits cap how many businesses can quote in each category and city, so you compete against fewer bidders than on big lead sites.</p>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section style={styles.compareSection}>
        <div className="section-container" style={styles.sectionContainer}>
          <div style={styles.sectionLabel}>COMPARISON</div>
          <h2 style={styles.sectionTitle}>QuoteBid vs. The Competition</h2>
          <div style={styles.compareTable}>
            <div style={styles.compareHeader}>
              <div style={styles.compareFeatureCol}>Feature</div>
              <div style={{ ...styles.compareColHeader, ...styles.compareColHighlightHeader }}>QuoteBid</div>
              <div style={styles.compareColHeader}>Angi</div>
              <div style={styles.compareColHeader}>Thumbtack</div>
            </div>
            {[
              ['AI job matching', '✅', '❌', '❌'],
              ['Flat monthly pricing', '✅', '❌', '❌'],
              ['Tiered early access', '✅', '❌', '❌'],
              ['Free for homeowners', '✅', '✅', '✅'],
              ['No pay-per-lead fees', '✅', '❌', '❌'],
              ['Starter plan free', '✅', '❌', '❌'],
            ].map(([feature, qb, angi, tt], i) => (
              <div key={i} style={{ ...styles.compareRow, background: i % 2 === 0 ? '#f8fafc' : '#ffffff' }}>
                <div style={styles.compareFeatureCol}>{feature}</div>
                <div style={{ ...styles.compareCol, ...styles.compareColHighlight }}>{qb}</div>
                <div style={styles.compareCol}>{angi}</div>
                <div style={styles.compareCol}>{tt}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER STORY */}
      <section style={styles.founderSection}>
        <div className="section-container" style={styles.sectionContainer}>
          <div style={styles.founderCard}>
            <h2 style={styles.founderHeading}>Why we created QuoteBid</h2>
            <p style={styles.founderBody}>I built QuoteBid because I got tired of watching the big lead-gen platforms charge contractors $80 a lead and treat homeowners like inventory. As a small business owner in Baton Rouge myself, I wanted a marketplace where homeowners post once, local pros compete fairly, and nobody pays just for a name. We're starting with 24 founding members in our hometown so we can earn this the right way.</p>
            <div style={styles.founderSignature}>— Larry Benoit, Founder</div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section style={styles.pricingSection}>
        <div className="section-container" style={styles.sectionContainer}>
          <div style={styles.sectionLabel}>PRICING</div>
          <h2 style={styles.sectionTitle}>Plans That Grow With You</h2>
          <p style={styles.pricingSubtitle}>Founding Member pricing — first 24 paid Founding Members only.</p>
          <div className="pricing-grid" style={styles.pricingGrid}>
            <div style={styles.pricingCard}>
              <div style={styles.pricingTier}>Starter</div>
              <div style={styles.pricingPrice}>Free</div>
              <div style={styles.pricingPeriod}>Forever</div>
              <ul style={styles.pricingFeatures}>
                <li>2 categories · 3 cities</li>
                <li>3 quotes per month</li>
                <li>View all requests</li>
                <li>Basic notifications</li>
                <li>No credit card required</li>
              </ul>
              <Link to="/register?type=business" style={styles.pricingBtnSecondary}>Get Started Free</Link>
            </div>
            <div className="pricing-card-featured" style={{ ...styles.pricingCard, ...styles.pricingCardFeatured }}>
              <div style={styles.pricingBadge}>Most Popular</div>
              <div style={styles.pricingTier}>Pro</div>
              <div style={styles.pricingPriceGroup}>
                <span style={styles.pricingStrike}>$249</span>
                <span style={styles.pricingPrice}>$124.50</span>
              </div>
              <div style={styles.pricingPeriod}>$124.50/mo · for life · (reg. $249/mo)</div>
              <ul style={styles.pricingFeatures}>
                <li>Up to 5 categories · up to 10 launch cities</li>
                <li>Unlimited quotes</li>
                <li>Priority notifications</li>
                <li>Email alerts on wins</li>
                <li>Beat Starter members</li>
              </ul>
              <Link to="/register?type=business&tier=pro" style={styles.pricingBtnPrimary}>Claim 50% Off →</Link>
            </div>
            <div style={styles.pricingCard}>
              <div style={styles.pricingTier}>Premium</div>
              <div style={styles.pricingPriceGroup}>
                <span style={styles.pricingStrike}>$399</span>
                <span style={styles.pricingPrice}>$199.50</span>
              </div>
              <div style={styles.pricingPeriod}>$199.50/mo · for life · (reg. $399/mo)</div>
              <ul style={styles.pricingFeatures}>
                <li>Up to 10 categories · up to 15 launch cities</li>
                <li>Everything in Pro</li>
                <li>30-min head start over Pro</li>
                <li>SMS notifications</li>
                <li>First to every hot job</li>
              </ul>
              <Link to="/register?type=business&tier=premium" style={styles.pricingBtnSecondary}>Claim 50% Off →</Link>
            </div>
          </div>
          <p style={styles.pricingNote}>Founding Member pricing is limited to the first 24 paid Founding Members. Once filled, pricing returns to standard rates.</p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section style={styles.finalCta}>
        <div style={styles.finalCtaContent}>
          <h2 style={styles.finalCtaTitle}>Ready to Change How You Get Work?</h2>
          <p style={styles.finalCtaSubtitle}>Built for Baton Rouge Metro homeowners and the local businesses that serve them.</p>
          <div className="final-cta-btns" style={styles.finalCtaBtns}>
            <Link to="/register?type=business" style={styles.finalCtaPrimary}>Start as a Business →</Link>
            <Link to="/new-request" style={styles.finalCtaSecondary}>Post a Request →</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  root: { fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif", color: '#0f172a', overflowX: 'hidden' },
  hero: { position: 'relative', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f4c75 100%)' },
  heroBg: { position: 'absolute', inset: '-20%', backgroundImage: `radial-gradient(ellipse at 20% 50%, rgba(59, 130, 246, 0.15) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(16, 185, 129, 0.1) 0%, transparent 60%), radial-gradient(ellipse at 50% 80%, rgba(99, 102, 241, 0.1) 0%, transparent 60%)` },
  heroGradient: { position: 'absolute', inset: 0, opacity: 0.3 },
  heroContent: { position: 'relative', zIndex: 2, textAlign: 'center', padding: '40px 24px', maxWidth: '800px' },
  heroBadge: { display: 'inline-block', background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(251, 191, 36, 0.1))', border: '1px solid rgba(251, 191, 36, 0.4)', color: '#fbbf24', padding: '8px 20px', borderRadius: '100px', fontSize: '13px', fontWeight: 600, marginBottom: '32px', letterSpacing: '0.02em' },
  heroTitle: { fontSize: 'clamp(40px, 7vw, 72px)', fontWeight: 800, color: '#ffffff', lineHeight: 1.1, marginBottom: '24px', letterSpacing: '-0.02em' },
  heroAccent: { background: 'linear-gradient(135deg, #3b82f6, #10b981)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' },
  heroSubtitle: { fontSize: 'clamp(16px, 2.5vw, 22px)', color: 'rgba(255,255,255,0.75)', lineHeight: 1.6, marginBottom: '40px' },
  heroTabs: { display: 'flex', gap: '8px', justifyContent: 'center', background: 'rgba(255,255,255,0.07)', borderRadius: '12px', padding: '6px', width: 'fit-content', margin: '0 auto 32px' },
  heroTab: { padding: '12px 24px', border: 'none', borderRadius: '8px', background: 'transparent', color: 'rgba(255,255,255,0.6)', fontSize: '15px', fontWeight: 500, cursor: 'pointer' },
  heroTabActive: { background: 'rgba(255,255,255,0.15)', color: '#ffffff', fontWeight: 700 },
  heroCtaGroup: { marginTop: '32px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' },
  heroPrimaryBtn: { display: 'inline-block', background: 'linear-gradient(135deg, #3b82f6, #10b981)', color: '#fff', padding: '18px 40px', borderRadius: '12px', textDecoration: 'none', fontSize: '18px', fontWeight: 700, boxShadow: '0 8px 32px rgba(59, 130, 246, 0.4)', letterSpacing: '-0.01em' },
  heroCtaNote: { color: 'rgba(255,255,255,0.5)', fontSize: '13px', margin: 0 },
  heroScroll: { position: 'absolute', bottom: '32px', left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', zIndex: 2 },
  heroScrollDot: { width: '6px', height: '6px', borderRadius: '50%', background: 'rgba(255,255,255,0.4)' },
  heroScrollText: { color: 'rgba(255,255,255,0.3)', fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase' },
  statsSection: { background: '#0f172a', padding: '48px 24px' },
  statsBanner: { textAlign: 'center', color: 'rgba(255,255,255,0.85)', fontSize: '20px', fontWeight: 600, marginBottom: '8px', letterSpacing: '0.02em' },
  statsSubheader: { textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '13px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '24px' },
  statsGrid: { maxWidth: '900px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0', flexWrap: 'wrap' },
  statCard: { flex: 1, minWidth: '160px', textAlign: 'center', padding: '24px' },
  statNumber: { fontSize: '48px', fontWeight: 800, background: 'linear-gradient(135deg, #3b82f6, #10b981)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', lineHeight: 1, marginBottom: '8px' },
  statLabel: { color: 'rgba(255,255,255,0.5)', fontSize: '13px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' },
  statDivider: { width: '1px', height: '60px', background: 'rgba(255,255,255,0.1)' },
  howSection: { padding: '100px 24px', background: '#ffffff' },
  sectionContainer: { maxWidth: '1100px', margin: '0 auto' },
  sectionLabel: { fontSize: '11px', fontWeight: 700, letterSpacing: '0.15em', color: '#3b82f6', textTransform: 'uppercase', marginBottom: '12px' },
  sectionTitle: { fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 800, color: '#0f172a', lineHeight: 1.1, letterSpacing: '-0.02em', marginBottom: '40px' },
  howTabs: { display: 'flex', gap: '8px', marginBottom: '60px', borderBottom: '2px solid #f1f5f9' },
  howTab: { padding: '12px 24px', border: 'none', borderBottom: '3px solid transparent', background: 'transparent', color: '#94a3b8', fontSize: '15px', fontWeight: 500, cursor: 'pointer', marginBottom: '-2px' },
  howTabActive: { color: '#0f172a', fontWeight: 700, borderBottomColor: '#3b82f6' },
  stepsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '32px', position: 'relative' },
  stepCard: { position: 'relative', padding: '40px 32px', background: '#f8fafc', borderRadius: '20px', border: '1px solid #e2e8f0' },
  stepNumber: { fontSize: '64px', fontWeight: 900, color: '#f1f5f9', lineHeight: 1, marginBottom: '16px', letterSpacing: '-0.04em' },
  stepIcon: { fontSize: '36px', marginBottom: '16px' },
  stepTitle: { fontSize: '20px', fontWeight: 700, color: '#0f172a', marginBottom: '12px' },
  stepDesc: { fontSize: '15px', color: '#64748b', lineHeight: 1.6 },
  stepConnector: { position: 'absolute', top: '50%', right: '-16px', width: '32px', height: '2px', background: 'linear-gradient(90deg, #3b82f6, #10b981)', zIndex: 1 },
  howCta: { textAlign: 'center', marginTop: '60px' },
  primaryBtn: { display: 'inline-block', background: 'linear-gradient(135deg, #3b82f6, #10b981)', color: '#fff', padding: '16px 36px', borderRadius: '12px', textDecoration: 'none', fontSize: '16px', fontWeight: 700, boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)' },
  whySection: { padding: '100px 24px', background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)' },
  whyGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', marginTop: '16px' },
  whyCard: { padding: '40px', background: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  whyIcon: { fontSize: '40px', marginBottom: '20px' },
  whyTitle: { fontSize: '20px', fontWeight: 700, color: '#0f172a', marginBottom: '12px' },
  whyDesc: { fontSize: '15px', color: '#64748b', lineHeight: 1.7 },
  compareSection: { padding: '100px 24px', background: '#f8fafc' },
  compareTable: { borderRadius: '16px', overflow: 'hidden', border: '1px solid #e2e8f0', marginTop: '40px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  compareHeader: { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', background: '#1e293b', padding: '16px 24px', gap: '16px' },
  compareRow: { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', padding: '16px 24px', gap: '16px', borderTop: '1px solid #e2e8f0' },
  compareFeatureCol: { color: '#0f172a', fontSize: '14px', fontWeight: 600 },
  compareColHeader: { textAlign: 'center' as const, color: 'rgba(255,255,255,0.7)', fontSize: '14px', fontWeight: 600 },
  compareColHighlightHeader: { color: '#10b981', fontWeight: 700 },
  compareCol: { textAlign: 'center' as const, color: '#64748b', fontSize: '16px' },
  compareColHighlight: { textAlign: 'center' as const, background: 'linear-gradient(135deg, rgba(59,130,246,0.08), rgba(16,185,129,0.08))', borderRadius: '8px', color: '#10b981', fontSize: '16px', fontWeight: 700 },
  founderSection: { padding: '100px 24px', background: '#ffffff' },
  founderCard: { maxWidth: '640px', margin: '0 auto', padding: '40px', background: '#f8fafc', borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  founderHeading: { fontSize: 'clamp(24px, 3vw, 32px)', fontWeight: 800, color: '#0f172a', lineHeight: 1.2, letterSpacing: '-0.02em', marginBottom: '20px', textAlign: 'center' as const },
  founderBody: { fontSize: '17px', color: '#334155', lineHeight: 1.7, marginBottom: '24px' },
  founderSignature: { fontSize: '15px', color: '#64748b', fontStyle: 'italic', textAlign: 'right' as const },
  pricingSection: { padding: '100px 24px', background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)' },
  pricingSubtitle: { fontSize: '16px', color: '#64748b', marginBottom: '60px' },
  pricingGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', alignItems: 'start' },
  pricingCard: { padding: '40px', background: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0', position: 'relative' as const },
  pricingCardFeatured: { border: '2px solid #3b82f6', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.15)', transform: 'scale(1.03)' },
  pricingBadge: { position: 'absolute' as const, top: '-14px', left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #3b82f6, #10b981)', color: '#fff', padding: '4px 20px', borderRadius: '100px', fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap' as const },
  pricingTier: { fontSize: '14px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' as const, letterSpacing: '0.1em', marginBottom: '16px' },
  pricingPriceGroup: { display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '4px' },
  pricingStrike: { fontSize: '20px', color: '#cbd5e1', textDecoration: 'line-through' },
  pricingPrice: { fontSize: '48px', fontWeight: 800, color: '#0f172a', lineHeight: 1 },
  pricingPeriod: { fontSize: '13px', color: '#94a3b8', marginBottom: '32px' },
  pricingFeatures: { listStyle: 'none', padding: 0, margin: '0 0 32px 0', display: 'flex', flexDirection: 'column' as const, gap: '12px' },
  pricingBtnPrimary: { display: 'block', background: 'linear-gradient(135deg, #3b82f6, #10b981)', color: '#fff', padding: '14px 24px', borderRadius: '10px', textDecoration: 'none', fontSize: '15px', fontWeight: 700, textAlign: 'center' as const },
  pricingBtnSecondary: { display: 'block', background: '#f8fafc', color: '#0f172a', padding: '14px 24px', borderRadius: '10px', textDecoration: 'none', fontSize: '15px', fontWeight: 700, textAlign: 'center' as const, border: '1px solid #e2e8f0' },
  pricingNote: { textAlign: 'center' as const, marginTop: '40px', fontSize: '14px', color: '#94a3b8' },
  finalCta: { background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)', padding: '120px 24px', textAlign: 'center' as const },
  finalCtaContent: { maxWidth: '600px', margin: '0 auto' },
  finalCtaTitle: { fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 800, color: '#ffffff', lineHeight: 1.1, letterSpacing: '-0.02em', marginBottom: '16px' },
  finalCtaSubtitle: { fontSize: '18px', color: 'rgba(255,255,255,0.6)', marginBottom: '40px' },
  finalCtaBtns: { display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' as const },
  finalCtaPrimary: { display: 'inline-block', background: 'linear-gradient(135deg, #3b82f6, #10b981)', color: '#fff', padding: '16px 32px', borderRadius: '12px', textDecoration: 'none', fontSize: '16px', fontWeight: 700, boxShadow: '0 4px 16px rgba(59, 130, 246, 0.4)' },
  finalCtaSecondary: { display: 'inline-block', background: 'rgba(255,255,255,0.1)', color: '#fff', padding: '16px 32px', borderRadius: '12px', textDecoration: 'none', fontSize: '16px', fontWeight: 700, border: '1px solid rgba(255,255,255,0.2)' },
};

export default Home;

